import { create } from 'zustand';
import type {
  ScreenRole,
  MonitorRoleConfig,
  SyncPreferences,
  PersistedMonitorConfig,
} from './types';
import { DEFAULT_SYNC_PREFERENCES } from './types';
import { rpcCall } from '../../api/rpc';

// ---------------------------------------------------------------------------
// Persistence helpers
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'multiMonitorConfig';

async function saveConfig(config: PersistedMonitorConfig): Promise<void> {
  await rpcCall<boolean>('setjson', { key: STORAGE_KEY, value: config });
}

async function loadConfig(): Promise<PersistedMonitorConfig | null> {
  try {
    return await rpcCall<PersistedMonitorConfig | null>('getjson', { key: STORAGE_KEY });
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Store interface
// ---------------------------------------------------------------------------

interface ScreenInfo {
  label: string;
  width: number;
  height: number;
  left: number;
  top: number;
  isPrimary: boolean;
}

interface MonitorConfigState {
  /** Detected screens (populated after permission grant) */
  screens: ScreenInfo[];
  /** Per-screen configuration */
  configs: MonitorRoleConfig[];
  /** Sync preferences */
  syncPreferences: SyncPreferences;
  /** Permission state */
  permissionState: 'prompt' | 'granted' | 'denied';
  /** Whether the config dialog is open */
  dialogOpen: boolean;
  /** Whether we're currently saving/loading */
  loading: boolean;

  // Actions
  setScreens: (screens: ScreenInfo[]) => void;
  setPermissionState: (state: 'prompt' | 'granted' | 'denied') => void;
  setScreenRole: (screenLabel: string, role: ScreenRole) => void;
  setScreenLayout: (screenLabel: string, rows: number, cols: number) => void;
  setSyncPreference: <K extends keyof SyncPreferences>(key: K, value: SyncPreferences[K]) => void;
  setDialogOpen: (open: boolean) => void;
  save: () => Promise<void>;
  load: () => Promise<void>;
  reset: () => void;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useMonitorConfigStore = create<MonitorConfigState>((set, get) => ({
  screens: [],
  configs: [],
  syncPreferences: { ...DEFAULT_SYNC_PREFERENCES },
  permissionState: 'prompt',
  dialogOpen: false,
  loading: false,

  setScreens: (screens) => {
    // Auto-initialize configs for new screens (single atomic set)
    const existing = get().configs;
    const configs = screens.map((screen): MonitorRoleConfig => {
      const found = existing.find((c) => c.screenLabel === screen.label);
      if (found) return found;
      // Default: primary screen gets worklist, others get secondary-viewer
      return {
        screenLabel: screen.label,
        role: screen.isPrimary ? 'worklist' : 'secondary-viewer',
        defaultLayout: { rows: 1, cols: 1 },
      };
    });
    set({ screens, configs });
  },

  setPermissionState: (permissionState) => set({ permissionState }),

  setScreenRole: (screenLabel, role) => {
    set((state) => {
      const exists = state.configs.some((c) => c.screenLabel === screenLabel);
      if (exists) {
        return { configs: state.configs.map((c) => c.screenLabel === screenLabel ? { ...c, role } : c) };
      }
      // Create entry if it doesn't exist yet
      return { configs: [...state.configs, { screenLabel, role, defaultLayout: { rows: 1, cols: 1 } }] };
    });
  },

  setScreenLayout: (screenLabel, rows, cols) => {
    set((state) => {
      const exists = state.configs.some((c) => c.screenLabel === screenLabel);
      if (exists) {
        return { configs: state.configs.map((c) => c.screenLabel === screenLabel ? { ...c, defaultLayout: { rows, cols } } : c) };
      }
      return { configs: [...state.configs, { screenLabel, role: 'unassigned' as const, defaultLayout: { rows, cols } }] };
    });
  },

  setSyncPreference: (key, value) => {
    set((state) => ({
      syncPreferences: { ...state.syncPreferences, [key]: value },
    }));
  },

  setDialogOpen: (dialogOpen) => set({ dialogOpen }),

  save: async () => {
    const { configs, syncPreferences } = get();
    set({ loading: true });
    try {
      await saveConfig({
        version: 1,
        updatedAt: new Date().toISOString(),
        screens: configs.map((c) => ({
          screenLabel: c.screenLabel,
          role: c.role,
          defaultLayout: c.defaultLayout,
        })),
        syncPreferences,
      });
    } finally {
      set({ loading: false });
    }
  },

  load: async () => {
    set({ loading: true });
    try {
      const config = await loadConfig();
      if (config) {
        set({
          configs: config.screens.map((s) => ({
            screenLabel: s.screenLabel,
            role: s.role,
            defaultLayout: s.defaultLayout,
          })),
          syncPreferences: config.syncPreferences ?? { ...DEFAULT_SYNC_PREFERENCES },
        });
      }
    } finally {
      set({ loading: false });
    }
  },

  reset: () => {
    const { screens } = get();
    set({
      configs: screens.map((screen): MonitorRoleConfig => ({
        screenLabel: screen.label,
        role: screen.isPrimary ? 'worklist' : 'secondary-viewer',
        defaultLayout: { rows: 1, cols: 1 },
      })),
      syncPreferences: { ...DEFAULT_SYNC_PREFERENCES },
    });
  },
}));
